/**
 * RFC-0392: WebSocket connection for real-time chat events.
 *
 * Replaces the SSE streaming in useAgentChat. Maintains a persistent
 * WebSocket connection to /ws/chat/{projectId} with automatic reconnection
 * and exponential backoff.
 *
 * Terminal error code 4001 (missing auth) suppresses reconnection.
 * 4003 (expired token) allows reconnection — getAuthToken() fetches a fresh token.
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { getConfiguredApiBase, getAuthToken } from '../api/client'
import { createDebug } from '../utils/debugLog'

const log = createDebug('dw:chat:socket')
const wsEventLog = createDebug('dw:chat:ws-event')

export interface ChatEvent {
  type: string
  [key: string]: unknown
}

interface UseChatSocketOptions {
  projectId: string
  enabled?: boolean
  onEvent: (event: ChatEvent) => void
}

interface UseChatSocketResult {
  connected: boolean
}

function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

const MIN_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000

/** Close codes that indicate a terminal auth error — retrying won't help.
 *  4003 (expired token) is NOT terminal: reconnect fetches a fresh token via getAuthToken(). */
const TERMINAL_CLOSE_CODES = new Set([4001])

export function useChatSocket({
  projectId,
  enabled = true,
  onEvent,
}: UseChatSocketOptions): UseChatSocketResult {
  const [connected, setConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectDelayRef = useRef(MIN_RECONNECT_DELAY)
  const onEventRef = useRef(onEvent)
  const mountedRef = useRef(true)
  /** Set to true when a terminal error event is received — suppresses reconnect. */
  const terminalErrorRef = useRef(false)
  /** Tracks how many times this connection has reconnected (for diagnostics). */
  const reconnectCountRef = useRef(0)

  onEventRef.current = onEvent

  const cleanup = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    setConnected(false)
  }, [])

  useEffect(() => {
    mountedRef.current = true
    terminalErrorRef.current = false
    if (!enabled || !projectId) {
      cleanup()
      return
    }

    let cancelled = false

    const connect = async () => {
      if (cancelled) return

      const apiBase = getConfiguredApiBase()
      const wsBase = toWsUrl(apiBase)
      const token = await getAuthToken()
      if (cancelled) return

      const params = new URLSearchParams()
      if (token) {
        params.set('token', token)
      }
      const queryString = params.toString()
      const url = `${wsBase}/ws/chat/${projectId}${queryString ? `?${queryString}` : ''}`

      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        if (cancelled) return
        log('connected projectId=%s', projectId)
        wsEventLog('connect projectId=%s reconnectCount=%d', projectId, reconnectCountRef.current)
        setConnected(true)
        reconnectDelayRef.current = MIN_RECONNECT_DELAY
      }

      ws.onmessage = (event) => {
        if (cancelled) return
        try {
          const data = JSON.parse(event.data)
          if (data.type === 'keepalive' || data.type === 'pong') return

          wsEventLog('message type=%s', data.type)

          // Handle server-sent error events (accept-first error handling).
          // Terminal auth errors (4001) suppress reconnection.
          if (data.type === 'error' && typeof data.code === 'number') {
            if (TERMINAL_CLOSE_CODES.has(data.code)) {
              terminalErrorRef.current = true
              wsEventLog('terminal-error code=%d', data.code)
            }
          }

          onEventRef.current(data)
        } catch {
          // ignore non-JSON messages (e.g. "pong")
        }
      }

      ws.onclose = (event) => {
        if (cancelled) return
        log('disconnected code=%d reason=%s', event.code, event.reason)
        wsEventLog('disconnect code=%d reason=%s wasTerminal=%s', event.code, event.reason, terminalErrorRef.current || TERMINAL_CLOSE_CODES.has(event.code))
        setConnected(false)

        // Don't reconnect on terminal auth errors — they won't resolve.
        if (terminalErrorRef.current || TERMINAL_CLOSE_CODES.has(event.code)) {
          return
        }

        scheduleReconnect()
      }

      ws.onerror = () => {
        // onclose will fire after onerror, which handles reconnection
      }
    }

    const scheduleReconnect = async () => {
      if (cancelled || !mountedRef.current) return
      const delay = reconnectDelayRef.current
      reconnectCountRef.current++
      log('reconnect in %dms', delay)
      wsEventLog('reconnect-scheduled delayMs=%d attempt=%d', delay, reconnectCountRef.current)
      reconnectDelayRef.current = Math.min(delay * 2, MAX_RECONNECT_DELAY)
      reconnectTimerRef.current = setTimeout(() => {
        if (!cancelled && mountedRef.current) {
          connect()
        }
      }, delay)
    }

    connect()

    return () => {
      cancelled = true
      mountedRef.current = false
      cleanup()
    }
  }, [projectId, enabled, cleanup])

  return { connected }
}
